import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Select, NumberInput, Button, Group, Stack, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import companyMutation from '@/api/mutitations/company';
import importRuleMutation from '@/api/mutitations/importRule';
import intakeEventMutations from '@/api/mutitations/intakeEvent';
import prepareTranslate from '@/helpers/dictionary';
import { MONTHS } from '@/helpers/enums';

const OVERRIDE_CODES = ['NO_COMPANY', 'NO_YEAR', 'NO_MONTH', 'NO_REFERENCE_YEAR', 'AMBIGUOUS', 'NO_RULE', 'SHEET_NOT_FOUND'];
const RETRY_ONLY_CODES = ['PROCESS_FAILURE', 'IMPORT_RULE_FAILURE'];

// Each sheet the rule expected and was not found, with the sheets found in its file
const getMissingSheets = (details) => {
  try {
    const missing = JSON.parse(details || '[]')
      .flatMap((file) => file.sheets || [])
      .filter((sheet) => sheet.type === 'SHEET_NOT_FOUND');
    return [...new Map(missing.map((sheet) => [sheet.expectedSheet, {
      expected: sheet.expectedSheet,
      available: sheet.availableSheets || [],
    }])).values()];
  } catch {
    return [];
  }
};

// The files the rule expected and the files that were received, from the event details
const getMissingFile = (details) => {
  try {
    const missing = JSON.parse(details || '[]')
      .filter((entry) => entry.type === 'IMPORT_RULE_FAILURE' && entry.expectedFile);
    return {
      expected: [...new Set(missing.map((entry) => entry.expectedFile))],
      available: [...new Set(missing.flatMap((entry) => entry.availableFiles || []))],
    };
  } catch {
    return { expected: [], available: [] };
  }
};

const ActionSection = ({ event, onSuccess }) => {
  const currentYear = new Date().getFullYear();
  const errorCodes = event.errorCodes?.length > 0
    ? event.errorCodes
    : [event.errorCode].filter(Boolean);
  const has = (code) => errorCodes.includes(code);
  const t = prepareTranslate();

  const [companyId, setCompanyId] = useState(null);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [referenceYear, setReferenceYear] = useState(null);
  const [ruleId, setRuleId] = useState(null);
  const [sheetChoices, setSheetChoices] = useState({});
  const missingSheets = getMissingSheets(event.details);
  const missingFile = getMissingFile(event.details);
  const [companies, setCompanies] = useState([]);
  const [rules, setRules] = useState([]);

  const { mutateAsync: getAllCompanies } = companyMutation.useGetAllCompanies();
  const { mutateAsync: getAllRules } = importRuleMutation.useGetAllImportRules();
  const { mutateAsync: retry, isPending: isRetrying } = intakeEventMutations.useRetryFilename();
  const {
    mutateAsync: updateEvent, isPending: isClosing,
  } = intakeEventMutations.useUpdateIntakeEvent();

  useEffect(() => {
    if (has('NO_COMPANY')) {
      getAllCompanies().then((res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        setCompanies(list.map((c) => ({ value: c._id, label: c.hebrew || c.name })));
      }).catch(() => {});
    }
    if (has('NO_RULE')) {
      getAllRules().then((res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        // When the company is known, only its rules are offered
        const ofCompany = list.filter(
          (r) => String(r.additionalData?.companyId) === String(event.overrides?.companyId),
        );
        const offered = event.overrides?.companyId && ofCompany.length > 0 ? ofCompany : list;
        setRules(offered.map((r) => ({ value: r._id, label: r.ruleName })));
      }).catch(() => {});
    }
    if (has('AMBIGUOUS')) {
      try {
        const parsed = JSON.parse(event.details || '{}');
        const candidates = parsed.candidates || [];
        setRules(candidates.map((c) => ({ value: c._id, label: c.name })));
      } catch {
        setRules([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id]);

  const nothingFilled = !companyId && !year && !month && !referenceYear && !ruleId
    && Object.values(sheetChoices).every((actual) => !actual);
  const isRetryDisabled = () => !event.fileRef?.s3Key;

  const handleRetry = async () => {
    try {
      await retry({
        intakeEventId: event._id,
        ...(companyId && { companyId }),
        ...(year && { year }),
        ...(month && { month: Number(month) }),
        ...(referenceYear && { referenceYear }),
        ...(ruleId && { ruleId }),
        ...(Object.values(sheetChoices).some(Boolean) && {
          sheetMap: Object.entries(sheetChoices)
            .filter(([, actual]) => actual)
            .map(([expected, actual]) => ({ expected, actual })),
        }),
      });
      notifications.show({ title: t('components.eventDrawer.notification.retrySuccess.title'), message: t('components.eventDrawer.notification.retrySuccess.message') });
      onSuccess();
    } catch {
      notifications.show({ title: t('components.eventDrawer.notification.retryError.title'), message: t('components.eventDrawer.notification.retryError.message') });
    }
  };

  const handleClose = async () => {
    try {
      await updateEvent({ id: event._id, status: 'closed' });
      onSuccess();
    } catch {
      notifications.show({ title: t('components.eventDrawer.notification.closeError.title'), message: t('components.eventDrawer.notification.closeError.message') });
    }
  };

  const showRetry = errorCodes.some(
    (c) => OVERRIDE_CODES.includes(c) || RETRY_ONLY_CODES.includes(c),
  );
  const retryIsOverride = errorCodes.some((c) => OVERRIDE_CODES.includes(c));

  return (
    <Stack gap="md" mt="md">
      {has('NO_COMPANY') && (
        <Select
          label={t('components.eventDrawer.fields.companyLabel')}
          placeholder={t('components.eventDrawer.fields.companyPlaceholder')}
          data={companies}
          value={companyId}
          onChange={setCompanyId}
          searchable
        />
      )}
      {has('NO_YEAR') && (
        <NumberInput
          label={t('components.eventDrawer.fields.yearLabel')}
          value={year}
          onChange={setYear}
          min={2000}
          max={currentYear}
        />
      )}
      {has('NO_MONTH') && (
        <Select
          label={t('components.eventDrawer.fields.monthLabel')}
          placeholder={t('components.eventDrawer.fields.monthPlaceholder')}
          data={MONTHS}
          value={month}
          onChange={setMonth}
        />
      )}
      {has('NO_REFERENCE_YEAR') && (
        <NumberInput
          label={t('components.eventDrawer.fields.referenceYearLabel')}
          value={referenceYear}
          onChange={setReferenceYear}
          min={2000}
          max={currentYear}
        />
      )}
      {(has('AMBIGUOUS') || has('NO_RULE')) && (
        <Select
          label={has('AMBIGUOUS') ? t('components.eventDrawer.fields.ambiguousLabel') : t('components.eventDrawer.fields.ruleLabel')}
          placeholder={t('components.eventDrawer.fields.selectPlaceholder')}
          data={rules}
          value={ruleId}
          onChange={setRuleId}
          searchable
        />
      )}
      {has('MISSING_FILE') && missingFile.expected.length > 0 && (
        <Text size="sm">
          {t('components.eventDrawer.fields.fileExpected', [
            missingFile.expected.join(', '),
            missingFile.available.join(', '),
          ])}
        </Text>
      )}
      {has('SHEET_NOT_FOUND') && missingSheets.map(({ expected, available }) => (
        <Stack key={expected} gap="xs">
          <Text size="sm">
            {t('components.eventDrawer.fields.sheetExpected', [expected, available.join(', ')])}
          </Text>
          <Select
            label={t('components.eventDrawer.fields.sheetPickLabel', [expected])}
            placeholder={t('components.eventDrawer.fields.selectPlaceholder')}
            data={available}
            value={sheetChoices[expected] || null}
            onChange={(value) => setSheetChoices((prev) => ({ ...prev, [expected]: value || '' }))}
          />
        </Stack>
      ))}
      <Group gap="sm">
        {showRetry && (
          <Button
            onClick={handleRetry}
            loading={isRetrying}
            disabled={isRetryDisabled() || (retryIsOverride && nothingFilled)}
          >
            {retryIsOverride ? t('components.eventDrawer.actions.retry') : t('components.eventDrawer.actions.retryAgain')}
          </Button>
        )}
        <Button
          variant="light"
          color="red"
          onClick={handleClose}
          loading={isClosing}
        >
          {t('components.eventDrawer.actions.closeEvent')}
        </Button>
      </Group>
    </Stack>
  );
};

ActionSection.propTypes = {
  event: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    errorCode: PropTypes.string,
    errorCodes: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.string,
    fileRef: PropTypes.shape({
      s3Key: PropTypes.string,
    }),
    overrides: PropTypes.shape({
      companyId: PropTypes.string,
    }),
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ActionSection;
